import { createFileRoute } from '@tanstack/react-router';
import {
  Boxes, Building2, CheckCircle, AlertCircle, Info, TrendingUp, Megaphone, ArrowRight, Filter, MoreVertical, Eye, Download, FileText
} from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { ComponentTabs } from '@/components/glitch-ui/component-tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import { ProgressIndicator } from '@/components/glitch-ui/progress-indicator';
import { EmptyState, PageErrorState, PageLoadingState } from '@/components/glitch-ui/empty-state';
import { DataTable } from '@/components/glitch-ui/data-table';
import { EditableField } from '@/components/glitch-ui/editable-field';
import { FilterDropdown } from '@/components/glitch-ui/filter-dropdown';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/_layout/components/tables')({
  component: TablesPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

function TablesPage() {
  const [progress] = useState(66);

  return (
    <div className="flex flex-col h-full">
      <ComponentTabs />

      <div className="flex-1 pb-8 space-y-8">
        {/* Basic Cards */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Cards</h2>

          {/* Standard Card with Documentation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-1">
                <CardTitle className="text-lg font-semibold text-gray-900">Standard Card Pattern</CardTitle>
                <p className="text-gray-900">
                  This is a basic card following GlitchAds design standards with proper spacing and styling.
                </p>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-gray-900 mt-3">
                  <li><code className="px-2 py-1 bg-gray-100 rounded">rounded-lg</code> (8px)</li>
                  <li><code className="px-2 py-1 bg-gray-100 rounded">border</code> (default gray-200)</li>
                  <li><code className="px-2 py-1 bg-gray-100 rounded">bg-white</code></li>
                  <li><code className="px-2 py-1 bg-gray-100 rounded">shadow-sm</code> (subtle drop shadow)</li>
                  <li><code className="px-2 py-1 bg-gray-100 rounded">hover:shadow-md</code> for interactive cards</li>
                </ol>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-1">
                <CardTitle className="text-lg font-semibold text-gray-900">Padding Standards</CardTitle>
                <p className="text-gray-900">
                  Consistent padding ensures proper spacing and visual hierarchy.
                </p>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside space-y-2 text-gray-900 mt-3">
                  <li>CardHeader: <code className="px-2 py-1 bg-gray-100 rounded">p-6 pb-1</code></li>
                  <li>CardContent: <code className="px-2 py-1 bg-gray-100 rounded">p-6 pt-0</code> (default)</li>
                  <li>With subtitle: Add subtitle inside CardHeader</li>
                  <li>No header: <code className="px-2 py-1 bg-gray-100 rounded">p-6</code> on CardContent</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Card Examples */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* Simple Card */}
            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-0">
                <CardTitle className="text-lg font-semibold text-gray-900">Simple Card</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-900">This is a simple card with a header and body content. It uses the standard card pattern with proper spacing.</p>
              </CardContent>
            </Card>

            {/* Card with Subtitle */}
            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-1">
                <CardTitle className="text-lg font-semibold text-gray-900">Card with Subtitle</CardTitle>
                <p className="text-sm text-gray-700">Supporting description text</p>
              </CardHeader>
              <CardContent>
                <p className="text-gray-900">The subtitle sits directly below the title (no gap) and uses <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">pb-1</code> on CardHeader for 4px separation from body content.</p>
              </CardContent>
            </Card>

            {/* Card with No Header */}
            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <p className="text-gray-900">Some cards don't need headers. When using CardContent alone, add <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">p-6</code> for proper padding on all sides.</p>
              </CardContent>
            </Card>

            {/* Card with Metrics */}
            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-0">
                <CardTitle className="text-lg font-semibold text-gray-900">Campaign Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-3xl font-semibold text-gray-900">1,234</div>
                  <p className="text-sm text-gray-700">Total Clicks</p>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  <span className="text-green-600 font-medium">+12.5%</span>
                  <span className="text-gray-700">from last month</span>
                </div>
              </CardContent>
            </Card>

            {/* Card with Progress */}
            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-0">
                <CardTitle className="text-lg font-semibold text-gray-900">Campaign Setup</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-gray-700">{progress}% complete</p>
              </CardContent>
            </Card>

            {/* Interactive Card */}
            <Card className="rounded-lg border bg-white hover:shadow-md transition-shadow cursor-pointer">
              <CardHeader className="p-6 pb-0">
                <CardTitle className="text-lg font-semibold text-gray-900">Interactive Card</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-900">Add <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">hover:shadow-md transition-shadow cursor-pointer</code> for clickable cards.</p>
              </CardContent>
            </Card>
          </div>

          {/* Empty States Section */}
          <h2 className="text-lg font-semibold text-gray-900 mb-4 mt-8">Empty States</h2>
          <p className="text-gray-900 mb-4">
            Use the <code className="px-2 py-1 bg-gray-100 rounded">EmptyState</code> component from <code className="px-2 py-1 bg-gray-100 rounded">@/components/glitch-ui/empty-state</code>.
            Color is automatically determined: <strong>passive states</strong> (no action) use <code className="px-2 py-1 bg-gray-100 rounded">text-gray-500</code>, <strong>active states</strong> (with action button) use <code className="px-2 py-1 bg-gray-100 rounded">text-gray-900</code>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Passive Empty State - No Action */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Passive Empty State (No Action)</h3>
              <EmptyState
                icon={<FileText className="h-12 w-12 text-gray-500" strokeWidth={1} />}
                title="No Reports Yet"
                description="Reports will appear here once generated"
              />
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-900 font-medium mb-2">Usage:</p>
                <pre className="text-xs text-gray-900 bg-gray-100 p-2 rounded overflow-x-auto">
{`<EmptyState
  icon={FileText}
  title="No Reports Yet"
  description="Reports will appear here once generated"
/>`}
                </pre>
              </div>
            </div>

            {/* Active Empty State - With Action */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Active Empty State (With Action)</h3>
              <EmptyState
                icon={<Megaphone className="h-12 w-12 text-gray-500" strokeWidth={1} />}
                title="No Campaigns Found"
                description="Get started by creating your first campaigns"
                action={
                  <Button
                    size="lg"
                    className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                  >
                    <span>CREATE CAMPAIGN</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                }
              />
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-900 font-medium mb-2">Usage:</p>
                <pre className="text-xs text-gray-900 bg-gray-100 p-2 rounded overflow-x-auto">
{`<EmptyState
  icon={Megaphone}
  title="No Campaigns Found"
  description="Get started by creating your first campaigns"
  action={<Button>CREATE CAMPAIGN</Button>}
/>`}
                </pre>
              </div>
            </div>
          </div>

          {/* Page Error State Section */}
          <h2 className="text-lg font-semibold text-gray-900 mb-4 mt-8">Page Error State</h2>
          <p className="text-gray-900 mb-4">
            Use <code className="px-2 py-1 bg-gray-100 rounded">PageErrorState</code> from <code className="px-2 py-1 bg-gray-100 rounded">@/components/glitch-ui/empty-state</code> when a page's primary data <strong>fails to load</strong> (4xx/5xx).
            This is deliberately distinct from <code className="px-2 py-1 bg-gray-100 rounded">PageEmptyState</code>: a load failure must never pose as missing data, or the user is told they have no campaigns/business/reports when the request actually errored. Red <code className="px-2 py-1 bg-gray-100 rounded">AlertCircle</code> plus an optional Retry button.
          </p>
          <div>
            <PageErrorState
              title="Couldn't Load Campaigns"
              description="This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists."
              onRetry={() => {}}
            />
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-900 font-medium mb-2">Usage:</p>
              <pre className="text-xs text-gray-900 bg-gray-100 p-2 rounded overflow-x-auto">
{`const { data, isError, refetch } = useQuery(...);

if (isError) {
  return (
    <PageErrorState
      title="Couldn't Load Campaigns"
      description="This page failed to load..."
      onRetry={() => refetch()}
    />
  );
}`}
              </pre>
            </div>
          </div>

          {/* Page Loading State Section */}
          <h2 className="text-lg font-semibold text-gray-900 mb-4 mt-8">Page Loading State</h2>
          <p className="text-gray-900 mb-4">
            Use <code className="px-2 py-1 bg-gray-100 rounded">PageLoadingState</code> from <code className="px-2 py-1 bg-gray-100 rounded">@/components/glitch-ui/empty-state</code> while a page's primary data <strong>loads</strong> or while the page redirects, and as a route's <code className="px-2 py-1 bg-gray-100 rounded">pendingComponent</code>.
            A page never renders nothing while it waits. A centred <code className="px-2 py-1 bg-gray-100 rounded">xl</code> cobalt <code className="px-2 py-1 bg-gray-100 rounded">Spinner</code> that appears only after 200ms, so a fast or cached load never flashes it; until then the box holds its height.
            It is the only page-level loading state. Two full-screen states are the exceptions: the Switching Organization overlay, which carries a label and must not wait, and the pages outside the app shell (onboarding, unsubscribe, OAuth consent).
          </p>
          <div>
            <Card className="rounded-lg border bg-white">
              <PageLoadingState />
            </Card>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-900 font-medium mb-2">Usage:</p>
              <pre className="text-xs text-gray-900 bg-gray-100 p-2 rounded overflow-x-auto">
{`const { data, isLoading } = useQuery(...);

if (isLoading) {
  return <PageLoadingState />;
}

// A route whose loader awaits data:
export const Route = createFileRoute('/...')({
  pendingComponent: PageLoadingState,
  pendingMs: 0,
  pendingMinMs: 0,
});`}
              </pre>
            </div>
          </div>

          {/* Accordion Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* Accordion Card */}
            <Card className="rounded-lg border bg-white">
              <CardHeader className="p-6 pb-1">
                <CardTitle className="text-lg font-semibold text-gray-900">Accordion</CardTitle>
                <p className="text-gray-900">Collapsible content sections for organizing information.</p>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger>Is it accessible?</AccordionTrigger>
                    <AccordionContent>
                      Yes. It adheres to the WAI-ARIA design pattern.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2">
                    <AccordionTrigger>Is it styled?</AccordionTrigger>
                    <AccordionContent>
                      Yes. It comes with default styles that matches the other components aesthetic.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3">
                    <AccordionTrigger>Is it animated?</AccordionTrigger>
                    <AccordionContent>
                      Yes. It's animated by default, but you can disable it if you prefer.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Dialogs & Overlays</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 flex flex-wrap gap-3">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline">Open Dialog</Button>
                </DialogTrigger>
                <DialogContent className="rounded-lg border bg-white shadow-sm" aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-gray-900">Dialog Example</DialogTitle>
                  </DialogHeader>
                  <p className="text-base text-gray-900">This is a dialog component that follows our standard card pattern.</p>
                  <div className="flex justify-end gap-3 mt-4">
                    <DialogTrigger asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogTrigger>
                    <Button className="bg-[#290BD9] hover:bg-[#290BD9]/90 text-white">Do it</Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline">Open Sheet</Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Sheet Example</SheetTitle>
                  </SheetHeader>
                  <p className="text-gray-900 mt-4">This is a sheet component.</p>
                </SheetContent>
              </Sheet>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline">Open Popover</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <p className="text-gray-900">This is a popover component.</p>
                </PopoverContent>
              </Popover>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">Open Menu</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem>Item 1</DropdownMenuItem>
                  <DropdownMenuItem>Item 2</DropdownMenuItem>
                  <DropdownMenuItem>Item 3</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardContent>
          </Card>
        </section>

        {/* Tab Card Panel with Table */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tab Card Panel with Table</h2>
          <p className="text-gray-900 mb-4">
            Tables inside a TabCardPanel pattern. The tabs attach to the card containing the table.
            Used for data-heavy pages like Assets, Competitors, and Reports.
          </p>

          {/* Live Example */}
          <div className="mb-6">
            {/* Tab Navigation + Action Buttons */}
            <div className="flex items-end justify-between">
              <div className="flex items-end gap-1">
                <button className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-white text-gray-900 border border-gray-200 border-b-transparent">
                  Landing Pages
                </button>
                <button className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-gray-100 text-gray-700 border border-transparent border-b-gray-200 hover:bg-gray-200 hover:text-gray-900 cursor-pointer">
                  Google
                </button>
                <button className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-gray-100 text-gray-700 border border-transparent border-b-gray-200 hover:bg-gray-200 hover:text-gray-900 cursor-pointer">
                  Audiences
                </button>
              </div>
              <div className="flex items-center gap-2 pb-1">
                <Button variant="outline" className="h-9">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </div>
            </div>

            {/* Card with Table Content */}
            <Card className="rounded-lg rounded-tl-none border bg-white">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ads</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50 cursor-pointer bg-[#290BD9]/10">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-base font-medium text-gray-900">Home Page</div>
                          <div className="text-sm text-gray-500">example.com</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">3</td>
                      </tr>
                      <tr className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-base font-medium text-gray-900">Pricing</div>
                          <div className="text-sm text-gray-500">example.com/pricing</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">2</td>
                      </tr>
                      <tr className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-base font-medium text-gray-900">Features</div>
                          <div className="text-sm text-gray-500">example.com/features</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Draft</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">—</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Documentation */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <p className="text-sm text-gray-900 font-medium">Key Differences from Standalone Tables:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
              <li><strong>Card wrapper:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">rounded-lg rounded-tl-none border bg-white</code> (no shadow, connects to tabs)</li>
              <li><strong>CardContent:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">p-0</code> (table has its own padding via cells)</li>
              <li><strong>No thead bg:</strong> Omit <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">bg-gray-50</code> on thead (cleaner look with tabs)</li>
              <li><strong>Selected row:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">bg-[#290BD9]/10</code> for selected state</li>
              <li><strong>Clickable rows:</strong> Add <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">cursor-pointer</code> to tr elements</li>
            </ul>
            <Separator className="my-3" />
            <p className="text-sm text-gray-900 font-medium">Cell Padding Standards:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
              <li><strong>Header cells:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">px-6 py-3</code></li>
              <li><strong>Body cells:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">px-6 py-4</code></li>
              <li><strong>Primary text:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">text-base font-medium text-gray-900</code></li>
              <li><strong>Secondary text:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">text-sm text-gray-500</code></li>
            </ul>
          </div>
        </section>

        {/* Tables with Status Badges */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tables with Status Badges (Standalone)</h2>
          <p className="text-gray-900 mb-4">
            Standalone tables with their own card wrapper. Used when table is not attached to tabs.
          </p>
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Summer Sale 2024</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">High</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Standard</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-500">15 Jan 2025</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Winter Promo</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Draft</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Medium</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Brand</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-500">22 Dec 2024</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Black Friday</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Paused</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Low</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Seasonal</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-500">01 Nov 2024</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Q1 Launch</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Stopped</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Urgent</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">Onboarding</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-500">03 Jan 2025</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Organizations Table Style */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Table with Avatar & Subheadings (Organizations Style)</h2>
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Members</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-[#290BD9]" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-base font-medium text-gray-900">Acme Corporation</div>
                          <div className="text-sm font-normal text-gray-500">@acme-corp</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">ENTERPRISE</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-900">12 members</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-500">15 Jan 2025</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-[#290BD9]" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-base font-medium text-gray-900">TechStart Labs</div>
                          <div className="text-sm font-normal text-gray-500">@techstart</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">SMB</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-900">5 members</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-500">22 Dec 2024</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-[#290BD9]" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-base font-medium text-gray-900">Digital Solutions Inc</div>
                          <div className="text-sm font-normal text-gray-500">@digital-solutions</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">FREE</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-900">3 members</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-500">08 Jan 2025</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-[#290BD9]" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-base font-medium text-gray-900">Growth Marketing Co</div>
                          <div className="text-sm font-normal text-gray-500">@growth-marketing</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">SMB</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-900">8 members</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-500">30 Nov 2024</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* List Section Headers */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">List Section Headers</h2>
          <p className="text-gray-900 mb-4">
            List section headers use identical styling to table headers for consistency. Use this pattern for sidebar lists, navigation lists, and any scrollable list container.
          </p>
          <div className="grid grid-cols-2 gap-6">
            {/* Standard List Section Header */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Standard List Section Header</h3>
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                {/* List Section Header - matches table header styling */}
                <div className="bg-gray-50 border-b px-6 py-3">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reports
                  </h3>
                </div>

                {/* List Content */}
                <div className="divide-y divide-gray-200">
                  <div className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="text-sm font-medium text-gray-900">Weekly Performance</div>
                    <div className="text-sm text-gray-500">Jan 6 - Jan 12, 2025</div>
                  </div>
                  <div className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="text-sm font-medium text-gray-900">Monthly Summary</div>
                    <div className="text-sm text-gray-500">December 2024</div>
                  </div>
                  <div className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="text-sm font-medium text-gray-900">Competitor Report</div>
                    <div className="text-sm text-gray-500">Jan 5, 2025</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky List Section Header */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Sticky List Section Header (for scrollable lists)</h3>
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                {/* Sticky List Section Header */}
                <div className="sticky top-0 bg-gray-50 border-b px-6 py-3 z-10">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaigns
                  </h3>
                </div>

                {/* Scrollable List Content */}
                <div className="overflow-y-auto max-h-48 divide-y divide-gray-200">
                  <div className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="text-sm font-medium text-gray-900">Summer Sale 2024</div>
                    <div className="text-sm text-gray-500">Active • 12 ads</div>
                  </div>
                  <div className="p-4 hover:bg-gray-50 cursor-pointer bg-[#290BD9]/10">
                    <div className="text-sm font-medium text-gray-900">Winter Promo</div>
                    <div className="text-sm text-gray-500">Draft • 5 ads</div>
                  </div>
                  <div className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="text-sm font-medium text-gray-900">Black Friday</div>
                    <div className="text-sm text-gray-500">Paused • 8 ads</div>
                  </div>
                  <div className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="text-sm font-medium text-gray-900">Q1 Launch</div>
                    <div className="text-sm text-gray-500">Draft • 3 ads</div>
                  </div>
                  <div className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="text-sm font-medium text-gray-900">Brand Campaign</div>
                    <div className="text-sm text-gray-500">Active • 1 ad</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-900 font-medium mb-2">Key Points:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
              <li>Header styling matches table headers: <code className="px-2 py-1 bg-gray-100 rounded text-xs">text-xs font-medium text-gray-500 uppercase tracking-wider</code></li>
              <li>Padding matches table headers: <code className="px-2 py-1 bg-gray-100 rounded text-xs">px-6 py-3</code></li>
              <li>Background: <code className="px-2 py-1 bg-gray-100 rounded text-xs">bg-gray-50</code> with <code className="px-2 py-1 bg-gray-100 rounded text-xs">border-b</code></li>
              <li>Add <code className="px-2 py-1 bg-gray-100 rounded text-xs">sticky top-0 z-10</code> for scrollable lists</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Navigation Patterns</h2>

          {/* Page Navigation Tabs */}
          <div className="mb-8">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Page Navigation Tabs</h3>
            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <p className="text-gray-900 mb-4">Full-width navigation tabs with bottom border (like the tabs at the top of this page).</p>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      <a href="#" className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm text-[#290BD9] border-[#290BD9]">
                        Active Tab
                      </a>
                      <a href="#" className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm text-gray-700 hover:text-gray-900 hover:border-gray-300 border-transparent">
                        Inactive Tab
                      </a>
                      <a href="#" className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm text-gray-700 hover:text-gray-900 hover:border-gray-300 border-transparent">
                        Another Tab
                      </a>
                    </nav>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-900 font-medium mb-2">Usage:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
                    <li>Main page navigation (e.g., Settings tabs, Component library tabs)</li>
                    <li>Active state: Cobalt text and bottom border (#290BD9)</li>
                    <li>Inactive state: Gray text with hover effects</li>
                    <li>Full-width border-bottom on container</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tab Card Panel */}
          <div className="mb-8">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Tab Card Panel (Attached Tabs)</h3>
            <p className="text-gray-900 mb-4">
              Tabs that visually connect to a card below. The active tab appears as part of the card while inactive tabs appear separate.
              Used for sidebar panels (Campaigns list, Reports list) and detail panels.
            </p>

            {/* Live Example - Two Column Layout */}
            <div className="grid grid-cols-2 gap-6 mb-4">
              {/* Left Panel - Static Tab */}
              <div>
                <div className="flex items-end justify-between">
                  <div className="flex items-end gap-1">
                    <button className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-white text-gray-900 border border-gray-200 border-b-transparent">
                      All Campaigns
                    </button>
                    <button className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-gray-100 text-gray-700 border border-transparent border-b-gray-200 hover:bg-gray-200 hover:text-gray-900 cursor-pointer">
                      New
                    </button>
                  </div>
                </div>
                <Card className="rounded-lg rounded-tl-none border bg-white">
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="p-2 bg-[#290BD9]/10 rounded text-sm text-gray-900">Campaign A (selected)</div>
                      <div className="p-2 hover:bg-gray-50 rounded text-sm text-gray-900 cursor-pointer">Campaign B</div>
                      <div className="p-2 hover:bg-gray-50 rounded text-sm text-gray-900 cursor-pointer">Campaign C</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Panel - Multiple Tabs with Actions */}
              <div>
                <div className="flex items-end justify-between">
                  <div className="flex items-end gap-1">
                    <button className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-white text-gray-900 border border-gray-200 border-b-transparent">
                      Keywords
                    </button>
                    <button className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-gray-100 text-gray-700 border border-transparent border-b-gray-200 hover:bg-gray-200 hover:text-gray-900 cursor-pointer">
                      Headlines
                    </button>
                    <button className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-gray-100 text-gray-700 border border-transparent border-b-gray-200 hover:bg-gray-200 hover:text-gray-900 cursor-pointer">
                      Settings
                    </button>
                  </div>
                  <div className="flex items-center gap-2 pb-1">
                    <Button variant="outline" className="h-9">
                      <Filter className="h-4 w-4 mr-2" />
                      Filter
                    </Button>
                  </div>
                </div>
                <Card className="rounded-lg rounded-tl-none border bg-white">
                  <CardContent className="p-4">
                    <p className="text-gray-900">Content area for the selected tab...</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Documentation */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <p className="text-sm text-gray-900 font-medium">Component:</p>
              <pre className="text-xs text-gray-900 bg-gray-100 p-3 rounded overflow-x-auto">
{`import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';

<TabCardPanel
  tabs={[
    { id: 'all', label: 'All Campaigns', isActive: true },
    { id: 'new', label: 'New', onClick: () => navigate('/new') },
  ]}
  actions={<Button variant="outline">Filter</Button>}
>
  <CampaignsList />
</TabCardPanel>`}
              </pre>
              <Separator className="my-3" />
              <p className="text-sm text-gray-900 font-medium">Tab Styling (when not using component):</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
                <li><strong>Active tab:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">bg-white text-gray-900 border border-gray-200 border-b-transparent</code></li>
                <li><strong>Inactive tab:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">bg-gray-100 text-gray-700 border border-transparent border-b-gray-200 hover:bg-gray-200 hover:text-gray-900</code></li>
                <li><strong>Both:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all</code></li>
                <li><strong>Card:</strong> <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">rounded-lg rounded-tl-none border bg-white</code></li>
                <li><strong>Actions:</strong> Place in <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">flex items-center gap-2 pb-1</code> on the right</li>
              </ul>
            </div>
          </div>

          {/* Content Tabs (Pill Style) */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-4">Content Tabs (Pill Style)</h3>
            <Tabs defaultValue="tab1" className="w-full">
              <TabsList>
                <TabsTrigger value="tab1">Tab 1</TabsTrigger>
                <TabsTrigger value="tab2">Tab 2</TabsTrigger>
                <TabsTrigger value="tab3">Tab 3</TabsTrigger>
              </TabsList>
              <TabsContent value="tab1">
                <Card className="rounded-lg border bg-white">
                  <CardContent className="p-6">
                    <p className="text-gray-900 mb-4">Content for Tab 1</p>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-900 font-medium mb-2">Usage:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
                        <li>Content switching within cards or sections</li>
                        <li>Pill-style with rounded background</li>
                        <li>Compact design for inline content organization</li>
                        <li>Use when tabs control content within a single container</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="tab2">
                <Card className="rounded-lg border bg-white">
                  <CardContent className="p-6">
                    <p className="text-gray-900">Content for Tab 2</p>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="tab3">
                <Card className="rounded-lg border bg-white">
                  <CardContent className="p-6">
                    <p className="text-gray-900">Content for Tab 3</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* Header Toolbars */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Header Toolbars</h2>
          <Card className="rounded-lg border bg-white mb-6">
            <CardHeader className="p-6 pb-1">
              <CardTitle className="text-lg font-semibold text-gray-900">Toolbar with Tabs, Search, and Filters</CardTitle>
              <p className="text-gray-900">All elements use consistent h-9 (36px) height for visual alignment.</p>
            </CardHeader>
            <CardContent className="p-6">
              {/* Live Example */}
              <div className="border rounded-lg p-4 bg-gray-50 mb-4">
                <div className="flex items-center justify-between py-4 h-[72px] bg-white rounded px-4">
                  <div className="flex items-center gap-3">
                    {/* Left side - typically empty or with breadcrumbs */}
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Tabs - uses default h-9 */}
                    <Tabs defaultValue="open">
                      <TabsList className="grid grid-cols-2">
                        <TabsTrigger value="open">Open</TabsTrigger>
                        <TabsTrigger value="archived">Archived</TabsTrigger>
                      </TabsList>
                    </Tabs>

                    {/* Search Input - override to h-9 */}
                    <div className="w-48">
                      <Input
                        type="text"
                        placeholder="Search..."
                        className="h-9 text-sm font-medium"
                      />
                    </div>

                    {/* Filter Button - uses default h-9 */}
                    <Button variant="outline" className="gap-2 text-gray-900">
                      <Filter className="h-4 w-4" />
                      <span>Filters</span>
                    </Button>

                    {/* Actions Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Eye className="h-4 w-4 mr-2" />
                          Mark all read
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Download className="h-4 w-4 mr-2" />
                          Export
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>

              {/* Documentation */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <p className="text-sm text-gray-900 font-medium">Critical Rules:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
                  <li><strong>All elements must be h-9 (36px)</strong> for visual consistency</li>
                  <li>TabsList: Uses default h-9 - do NOT override with h-8</li>
                  <li>Input: Override with <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">className="h-9"</code> (default is h-10)</li>
                  <li>Button: Uses default h-9 - do NOT use size="sm" (which sets h-8)</li>
                  <li>Use <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">gap-3</code> spacing between elements</li>
                </ul>
                <Separator className="my-3" />
                <p className="text-sm text-gray-900 font-medium">Common Mistakes:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
                  <li>❌ <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">TabsList className="h-8"</code> - Wrong height</li>
                  <li>❌ <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Input className="h-8"</code> - Wrong height</li>
                  <li>❌ <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Button size="sm"</code> - Sets h-8 and text-xs</li>
                  <li>✅ Use defaults for TabsList and Button</li>
                  <li>✅ Override Input to h-9 to match other elements</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Separators</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-900">Section 1</p>
              <Separator />
              <p className="text-gray-900">Section 2</p>
              <Separator />
              <p className="text-gray-900">Section 3</p>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Alerts (Standard Pattern)</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              <div className="border-l-4 rounded-lg p-4" style={{ backgroundColor: '#EDE9FE', borderLeftColor: '#290BD9', borderTop: '1px solid #C4B5FD', borderRight: '1px solid #C4B5FD', borderBottom: '1px solid #C4B5FD' }}>
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#290BD9' }} />
                  <div className="text-gray-900">
                    This is an informational alert message using Cobalt brand color.
                  </div>
                </div>
              </div>
              <div className="border-l-4 rounded-lg p-4" style={{ backgroundColor: '#D1FAE5', borderLeftColor: '#047857', borderTop: '1px solid #6EE7B7', borderRight: '1px solid #6EE7B7', borderBottom: '1px solid #6EE7B7' }}>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#047857' }} />
                  <div className="text-gray-900">
                    This is a success alert message for confirmations.
                  </div>
                </div>
              </div>
              <div className="border-l-4 rounded-lg p-4" style={{ backgroundColor: '#FEF3C7', borderLeftColor: '#A16207', borderTop: '1px solid #FDE68A', borderRight: '1px solid #FDE68A', borderBottom: '1px solid #FDE68A' }}>
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#A16207' }} />
                  <div className="text-gray-900">
                    This is a warning alert message for caution states.
                  </div>
                </div>
              </div>
              <div className="border-l-4 rounded-lg p-4" style={{ backgroundColor: '#FEE2E2', borderLeftColor: '#B91C1C', borderTop: '1px solid #FCA5A5', borderRight: '1px solid #FCA5A5', borderBottom: '1px solid #FCA5A5' }}>
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#B91C1C' }} />
                  <div className="text-gray-900">
                    This is an error alert message for destructive actions.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Alerts with Actions</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-4">
              {/* Error alert with primary action button */}
              <div className="bg-[#FEE2E2] border-l-4 border-l-[#FCA5A5] rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-white rounded-lg shadow p-2 self-start mt-0.5">
                    <AlertCircle className="h-5 w-5 text-[#B91C1C]" />
                  </div>
                  <div className="flex flex-1 items-center justify-between gap-6">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-1">Error Title</h3>
                      <p className="text-gray-900 text-sm">Description of what went wrong and what the action will do to fix it.</p>
                    </div>
                    <div className="shrink-0">
                      <Button
                        size="sm"
                        className="w-[180px] h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                      >
                        <span>REBUILD AD</span>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Warning alert with primary action button — larger variant */}
              <div className="bg-[#FEF3C7] border-l-4 border-l-[#FDE68A] rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-white rounded-lg shadow p-3 self-start mt-0.5">
                    <AlertCircle className="h-6 w-6 text-[#A16207]" />
                  </div>
                  <div className="flex flex-1 items-center justify-between gap-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Warning Title</h3>
                      <p className="text-gray-900">Description of what needs attention and what the action will do.</p>
                    </div>
                    <div className="shrink-0">
                      <Button
                        size="lg"
                        className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                      >
                        <span>TAKE ACTION</span>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Progress Indicators</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-6">
              <div>
                <p className="text-sm text-gray-700 mb-4">Steps Indicator</p>
                <StepsIndicator currentStep={3} totalSteps={5} />
              </div>
              <Separator />
              <div>
                <p className="text-sm text-gray-700 mb-4">Progress Indicator</p>
                <ProgressIndicator currentStep={3} totalSteps={5} />
              </div>
            </CardContent>
          </Card>
        </section>

        <DataTableSection />
        <EditableFieldSection />
      </div>
    </div>
  );
}

// --- DataTable Component Examples ---

type SampleRow = { name: string; status: string; clicks: number; cost: string };

const sampleData: SampleRow[] = [
  { name: 'Brand Campaign', status: 'Active', clicks: 1243, cost: '$456.78' },
  { name: 'Prospecting USA', status: 'Active', clicks: 892, cost: '$312.50' },
  { name: 'Competitor - Acme', status: 'Paused', clicks: 156, cost: '$89.20' },
  { name: 'Retargeting Q1', status: 'Active', clicks: 2104, cost: '$678.90' },
  { name: 'Display Network', status: 'Ended', clicks: 45, cost: '$12.30' },
  { name: 'Shopping Ads', status: 'Active', clicks: 3201, cost: '$1,024.50' },
];

const sampleColumns: ColumnDef<SampleRow, unknown>[] = [
  { accessorKey: 'name', header: 'Campaign' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        row.original.status === 'Active' ? 'bg-green-100 text-green-800' :
        row.original.status === 'Paused' ? 'bg-yellow-100 text-yellow-800' :
        'bg-gray-100 text-gray-800'
      }`}>
        {row.original.status}
      </span>
    ),
  },
  { accessorKey: 'clicks', header: 'Clicks' },
  { accessorKey: 'cost', header: 'Cost' },
];

function DataTableSection() {
  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">DataTable Component</h2>
      <div className="space-y-6">
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Bordered Variant (Default)</h3>
          <p className="text-sm text-gray-500 mb-4">Card with shadow, sortable headers, pagination. Used for standalone tables.</p>
          <DataTable
            data={sampleData}
            columns={sampleColumns}
            pageSize={4}
            initialSorting={[{ id: 'clicks', desc: true }]}
          />
        </div>

        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Flush Variant</h3>
          <p className="text-sm text-gray-500 mb-4">No background or shadow, bleeds to card edges. Used inside cards or panels.</p>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <DataTable
                data={sampleData.slice(0, 3)}
                columns={sampleColumns}
                variant="flush"
                pageSize={5}
              />
            </CardContent>
          </Card>
        </div>

        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Empty State</h3>
          <p className="text-sm text-gray-500 mb-4">Rich empty state with icon, title, and description.</p>
          <DataTable
            data={[]}
            columns={sampleColumns}
            emptyIcon={FileText}
            emptyTitle="No Campaigns"
            emptyDescription="Campaigns will appear here once created"
          />
        </div>
      </div>
    </section>
  );
}

// --- EditableField Component Examples ---

function EditableFieldSection() {
  const [headline, setHeadline] = useState('Expert Google Ads Management');
  const [headline2, setHeadline2] = useState('AI-Powered Campaign Optimization');
  const [headline3, setHeadline3] = useState('Get More Conversions Today');
  const [description, setDescription] = useState('Transform your advertising with AI-powered optimization that maximizes conversions and reduces cost per acquisition.');

  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">EditableField Component</h2>
      <div className="space-y-6">
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Single-Line with Character Limit</h3>
          <p className="text-sm text-gray-500 mb-4">Hover to reveal edit controls. Character counter turns red when over limit. Delete icon is always visible with red hover state.</p>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-1">
              <EditableField
                value={headline}
                onChange={setHeadline}
                onDelete={() => setHeadline('')}
                maxLength={30}
                placeholder="Enter headline"
              />
              <EditableField
                value={headline2}
                onChange={setHeadline2}
                onDelete={() => setHeadline2('')}
                maxLength={30}
                placeholder="Enter headline"
              />
              <EditableField
                value={headline3}
                onChange={setHeadline3}
                onDelete={() => setHeadline3('')}
                maxLength={30}
                placeholder="Enter headline"
              />
            </CardContent>
          </Card>
        </div>

        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Multiline with Auto-Resize</h3>
          <p className="text-sm text-gray-500 mb-4">Textarea mode with dynamic height adjustment. Expands as content grows.</p>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <EditableField
                value={description}
                onChange={setDescription}
                maxLength={90}
                placeholder="Enter description"
              />
            </CardContent>
          </Card>
        </div>

        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Disabled State</h3>
          <p className="text-sm text-gray-500 mb-4">Non-interactive, no hover effects or delete button.</p>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <EditableField
                value="This field cannot be edited"
                onChange={() => {}}
                disabled
              />
            </CardContent>
          </Card>
        </div>

        {/* FilterDropdown */}
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">FilterDropdown</h2>
          <Card className="rounded-lg border bg-white mb-6">
            <CardHeader className="p-6 pb-1">
              <CardTitle className="text-lg font-semibold text-gray-900">Reusable Filter Popover</CardTitle>
              <p className="text-gray-900">Standardised filter dropdown with sections, checkboxes, nested children, date range, and badge counter. Used across campaigns, reports, and optimizations.</p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="border rounded-lg p-4 bg-gray-50 mb-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">Basic (no date range):</span>
                  <FilterDropdown
                    sections={[
                      { id: 'status', title: 'Status', options: [
                        { value: 'active', label: 'Active' },
                        { value: 'draft', label: 'Draft' },
                        { value: 'archived', label: 'Archived' },
                      ]},
                      { id: 'type', title: 'Type', options: [
                        { value: 'blog', label: 'Blog Post' },
                        { value: 'page', label: 'Landing Page' },
                        { value: 'email', label: 'Email' },
                      ]},
                    ]}
                    selectedValues={{ status: ['active', 'draft'], type: ['blog'] }}
                    showDateRange={false}
                    popoverWidth="w-[240px]"
                    onApply={() => {}}
                  />
                  <span className="text-sm text-gray-500">With nested children:</span>
                  <FilterDropdown
                    sections={[
                      { id: 'reports', title: 'Report Type', options: [
                        { value: 'performance', label: 'Performance', children: [
                          { value: 'weekly', label: 'Weekly' },
                          { value: 'monthly', label: 'Monthly' },
                        ]},
                        { value: 'competitor', label: 'Competitor' },
                        { value: 'intelligence', label: 'Intelligence' },
                      ]},
                    ]}
                    selectedValues={{ reports: ['weekly', 'competitor'] }}
                    showDateRange={false}
                    popoverWidth="w-[260px]"
                    onApply={() => {}}
                  />
                  <span className="text-sm text-gray-500">With date range:</span>
                  <FilterDropdown
                    sections={[
                      { id: 'category', title: 'Category', options: [
                        { value: 'sales', label: 'Sales' },
                        { value: 'support', label: 'Support' },
                      ]},
                    ]}
                    selectedValues={{ category: ['sales'] }}
                    showDateRange={true}
                    popoverWidth="w-[400px]"
                    onApply={() => {}}
                  />
                </div>
              </div>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Features:</strong> Section header checkbox (select all), cobalt blue checked state, nested children support, badge counter, Clear button.</p>
                <p><strong>Import:</strong> <code className="bg-gray-100 px-1 rounded">{'import { FilterDropdown } from \'@/components/glitch-ui/filter-dropdown\''}</code></p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
